import { useMemo, useState } from "react";
import type { Student } from "../providers/studentsProvider";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { DateTime } from "luxon";
import { runSafelyWithServerClient } from "../api/server.api";
import LoadingButton from "@mui/lab/LoadingButton";
import { BooleanInput } from "../inputs/BooleanInput";
import { Form2 } from "../misc/Form2";
import { Column } from "../misc/Column";
import { DateInput } from "../inputs/DateInput";
import { SelectInput } from "../inputs/SelectInput";
import { createPdfPreview, PdfPreviewDialogContent, type PdfPreview } from "./PdfPreviewDialogContent";

export function CreateAusbildungsnachweisDialog(props: { student: Student } & DialogProps) {
  const { student, ...dialogProps } = props;
  const [preview, setPreview] = useState<undefined | PdfPreview>();
  const closeDialog = () => {
    setPreview(undefined);
    dialogProps.onClose();
  };

  return (
    <Dialog {...dialogProps} onClose={closeDialog} maxWidth={!preview ? "sm" : "lg"} fullWidth>
      <DialogTitle>{!preview ? "Ausbildungsnachweis erstellen" : "Vorschau"}</DialogTitle>
      <DialogCloseButton onClick={closeDialog} />
      {!preview ? (
        <_FormDialogContentAndActions student={student} setPreview={setPreview} />
      ) : (
        <PdfPreviewDialogContent
          student={student}
          preview={preview}
          documentName="Ausbildungsnachweis"
          documentNameWithArticle="des Ausbildungsnachweises"
          signatureRequestType="ausbildungsnachweis"
          closeDialog={closeDialog}
        />
      )}
    </Dialog>
  );
}

interface _FormValues {
  bookedTrainingId: string;
  dateOfCreation: DateTime | null;
  includePlannedAppointments: boolean;
}

function _FormDialogContentAndActions(props: { student: Student; setPreview: (preview: PdfPreview) => void }) {
  const { student, setPreview } = props;
  const [creatingPreview, setCreatingPreview] = useState(false);
  const relevantBookedTrainings = useMemo(
    () => student.bookedTrainings.filter((it) => it.isForDrivingLicense),
    [student],
  );
  const initiallySelectedBookedTrainingId = useMemo(() => {
    if (relevantBookedTrainings.length === 0) {
      return "";
    }
    let initiallySelectedBookedTraining = relevantBookedTrainings[0];
    let t = initiallySelectedBookedTraining.kpis?.mostRecentDrivingLessonDate ?? _DateTime0;
    for (const bookedTraining of relevantBookedTrainings) {
      const t2 = bookedTraining.kpis?.mostRecentDrivingLessonDate ?? _DateTime0;
      if (t2 > t) {
        initiallySelectedBookedTraining = bookedTraining;
        t = t2;
      }
    }
    return initiallySelectedBookedTraining.id;
  }, [relevantBookedTrainings]);

  const createPreview = async (formData: _FormValues) => {
    const { bookedTrainingId, dateOfCreation, includePlannedAppointments } = formData;
    setCreatingPreview(true);
    await runSafelyWithServerClient(
      async (serverClient) => {
        const { drivingLicenseClass } = student.bookedTrainingsById[bookedTrainingId];
        const preview = await createPdfPreview({
          serverClient,
          createPdf: () =>
            serverClient.createAusbildungsnachweis({
              params: { studentId: student.id, drivingLicenseClass },
              queries: {
                includePlannedAppointments,
                ...(dateOfCreation ? { dateOfCreation: dateOfCreation.toISODate() } : {}),
              },
            }),
        });
        setPreview(preview);
      },
      {
        errorMessage: "Fehler beim Erstellen der Vorschau.",
        successMessage: "",
      },
    );
    setCreatingPreview(false);
  };

  return (
    <Form2
      onSubmit={createPreview}
      defaultValues={{
        bookedTrainingId: initiallySelectedBookedTrainingId,
        dateOfCreation: null,
        includePlannedAppointments: false,
      }}
    >
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {`Du kannst hier den Ausbildungsnachweis für ${student.callName} ohne Unter\u00ADschrift erstellen und zur Unter\u00ADschriften\u00ADmappe des Fahrschul\u00ADleiters hinzufügen.`}
        </Alert>
        <Column sx={{ gap: 2 }}>
          {relevantBookedTrainings.length > 0 && (
            <SelectInput
              label="für Führerscheinklasse"
              size="small"
              source="bookedTrainingId"
              options={relevantBookedTrainings.map((it) => [it.id, it.drivingLicenseClass])}
            />
          )}
          <DateInput source="dateOfCreation" size="small" allowPastDates required={false} />
          <BooleanInput source="includePlannedAppointments" label="Inkl. geplanter Termine" />
        </Column>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" loading={creatingPreview} type="submit">
          Vorschau
        </LoadingButton>
      </DialogActions>
    </Form2>
  );
}

const _DateTime0 = DateTime.fromMillis(0);
